import { BotPendingStatus, BotPendingSurveyItem } from '../../api/admin.api';
import { Button } from '../ui/button';

/** 20:30 첫 생성분의 attemptNo (2~ = 다시 만들기) */
const FIRST_ATTEMPT_NO = 1;
const FIRST_ATTEMPT_LABEL = '20:30';

/** 표 헤더 = 한 행의 칸 구성. colSpan 을 길이로 공유해 열이 늘어도 실패 사유 줄이 어긋나지 않는다 */
export const PENDING_COLUMNS = [
  { label: '대상일', align: 'text-left' },
  { label: '생성 시각', align: 'text-left' },
  { label: '몇 번째', align: 'text-left' },
  { label: '설문 문장', align: 'text-left' },
  { label: '선택지', align: 'text-left' },
  { label: '도메인', align: 'text-left' },
  { label: '상태', align: 'text-left' },
  { label: '액션', align: 'text-center' },
] as const;

const STATUS_BADGES: Record<BotPendingStatus, { label: string; className: string }> = {
  PENDING: { label: '대기', className: 'bg-orange-50 text-orange-700' },
  PUBLISHED: { label: '발행됨', className: 'bg-green-50 text-green-700' },
  REJECTED: { label: '반려', className: 'bg-gray-100 text-gray-700' },
  EXPIRED: { label: '만료', className: 'bg-gray-100 text-gray-400' },
};

const pad2 = (n: number) => n.toString().padStart(2, '0');

const formatDateTime = (dt: string) => {
  const d = new Date(dt);
  return `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** yyyy-MM-dd → MM-dd (문자열 그대로 잘라 시간대 변환을 피한다) */
const formatTargetDate = (date: string) => date.slice(5);

const getAttemptLabel = (attemptNo: number) =>
  attemptNo === FIRST_ATTEMPT_NO ? FIRST_ATTEMPT_LABEL : `다시 만들기 ${attemptNo - FIRST_ATTEMPT_NO}`;

interface BotPendingRowProps {
  item: BotPendingSurveyItem;
  /** 다시 만들기 남은 횟수. null 이면 버튼을 보이지 않는다 (그날 마지막 반려 행 + 오늘·어제만) */
  remainingRegenerate: number | null;
  /** 이 행에 걸린 요청이 진행 중이면 버튼을 모두 막는다 */
  busy: boolean;
  isRegenerating: boolean;
  onApprove: (item: BotPendingSurveyItem) => void;
  onReject: (item: BotPendingSurveyItem) => void;
  onRegenerate: (item: BotPendingSurveyItem) => void;
}

/** 🔴 원문 표시 전용 — 문구 수정 UI 를 두지 않는다 (spec §4.9.4) */
export const BotPendingRow = ({
  item,
  remainingRegenerate,
  busy,
  isRegenerating,
  onApprove,
  onReject,
  onRegenerate,
}: BotPendingRowProps) => {
  const badge = STATUS_BADGES[item.status] ?? { label: item.status, className: 'bg-gray-100 text-gray-700' };

  const renderActions = () => {
    if (item.status === 'PENDING') {
      return (
        <div className="flex items-center justify-center gap-1.5">
          <Button size="sm" onClick={() => onApprove(item)} disabled={busy}>
            승인하고 발행
          </Button>
          <Button size="sm" variant="outline" className="text-red-600" onClick={() => onReject(item)} disabled={busy}>
            반려
          </Button>
        </div>
      );
    }
    if (item.status === 'PUBLISHED' && item.questionId) {
      return <span className="text-gray-500">#{item.questionId}</span>;
    }
    if (remainingRegenerate !== null) {
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onRegenerate(item)}
          disabled={busy || remainingRegenerate <= 0}
        >
          {isRegenerating ? '만드는 중…' : `다시 만들기 (남은 ${Math.max(remainingRegenerate, 0)}회)`}
        </Button>
      );
    }
    return <span className="text-gray-300">—</span>;
  };

  return (
    <>
      <tr className={`hover:bg-gray-50 ${item.failReason ? '' : 'border-b'}`}>
        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatTargetDate(item.targetDate)}</td>
        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDateTime(item.regDateTime)}</td>
        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{getAttemptLabel(item.attemptNo)}</td>
        {/* 🔴 extraContent 도 승인 시 함께 게시된다 (BotSurveyPublisher) — 검수 대상이라 같이 보여준다 */}
        <td className="px-4 py-3 text-gray-900 font-medium min-w-[16rem]">
          <div>{item.description}</div>
          {item.extraContent && (
            <div className="mt-1 text-xs font-normal text-gray-500 whitespace-pre-wrap">{item.extraContent}</div>
          )}
        </td>
        <td className="px-4 py-3 text-gray-700">
          <ol className="space-y-0.5">
            {item.options.map((option, index) => (
              <li key={index} className="whitespace-nowrap">
                {index + 1}. {option}
              </li>
            ))}
          </ol>
        </td>
        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{item.domain ?? '—'}</td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
            {badge.label}
          </span>
        </td>
        <td className="px-4 py-3 text-center whitespace-nowrap">{renderActions()}</td>
      </tr>
      {item.failReason && (
        <tr className="border-b">
          <td colSpan={PENDING_COLUMNS.length} className="px-4 pb-3 pt-0 text-xs text-red-600">
            최근 실패 사유: {item.failReason}
          </td>
        </tr>
      )}
    </>
  );
};
